import { useState, useRef, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const MentionText = ({ text, mentions }) => {
  const names = (mentions || []).map(m => m.name).filter(Boolean);
  if (!text || names.length === 0) return <>{text}</>;
  const pattern = new RegExp(`(@(?:${names.map(escapeRegex).join('|')}))`, 'g');
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('@') && names.includes(part.slice(1)) ? (
          <span key={i} className="font-semibold text-[#D4A373]">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export default function MentionTextarea({
  value,
  onChange,
  participants,
  onMentionsChange,
  currentUserId,
  placeholder,
  rows = 2,
  className = "",
  dataTestId,
  autoFocus = false
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(null);
  const [mentions, setMentions] = useState([]);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (value === "" && mentions.length > 0) {
      setMentions([]);
      onMentionsChange([]);
    }
  }, [value]);

  const syncMentions = (list, text) => {
    const active = list.filter(m => text.includes(`@${m.name}`));
    setMentions(active);
    onMentionsChange(active);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    syncMentions(mentions, text);
    const cursor = e.target.selectionStart;
    const before = text.slice(0, cursor);
    const atIndex = before.lastIndexOf('@');
    if (atIndex >= 0 && (atIndex === 0 || /\s/.test(before[atIndex - 1]))) {
      const q = before.slice(atIndex + 1);
      if (!q.includes('\n') && q.length <= 30) {
        setQuery(q);
        setMentionStart(atIndex);
        setShowDropdown(true);
        return;
      }
    }
    setShowDropdown(false);
  };

  const selectParticipant = (p) => {
    const cursor = textareaRef.current?.selectionStart ?? value.length;
    const newText = value.slice(0, mentionStart) + `@${p.name} ` + value.slice(cursor);
    onChange(newText);
    const newList = mentions.some(m => m.user_id === p.user_id)
      ? mentions
      : [...mentions, { user_id: p.user_id, name: p.name }];
    syncMentions(newList, newText);
    setShowDropdown(false);
    textareaRef.current?.focus();
  };

  const filtered = (participants || [])
    .filter(p => p.user_id !== currentUserId)
    .filter(p => p.name?.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 6);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        placeholder={placeholder}
        rows={rows}
        data-testid={dataTestId}
        className={className}
        autoFocus={autoFocus}
      />
      {showDropdown && filtered.length > 0 && (
        <div
          className="absolute z-50 bottom-full mb-1 left-0 w-64 bg-white border border-[#CCD5AE] rounded-xl shadow-lg overflow-hidden"
          data-testid="mention-dropdown"
        >
          {filtered.map((p) => (
            <button
              type="button"
              key={p.user_id}
              onMouseDown={(e) => { e.preventDefault(); selectParticipant(p); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#E9EDC9]/60 transition-colors"
              data-testid={`mention-option-${p.user_id}`}
            >
              {p.picture ? (
                <img src={p.picture} alt={p.name} className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#3A5A40]/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#3A5A40]">{p.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
              <span className="text-sm text-[#344E41] font-medium truncate">{p.name}</span>
              <span className="ml-auto text-xs text-[#A3B18A] capitalize">{p.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
